import React from 'react';
import { InfoBox, ExerciseBox, CodeBlock, InlineCode, Result, SectionTitle, Image, Separator, CorrectionBox, Note, TableContainer, Highlight, PatternContainer, SectionHeading, SubHeading, TwoColGrid, ThreeColGrid, GridItem, Divider } from '../../../../components/course/CourseComponents';
import screen2 from '../screen2.jpg';
import screen3 from '../screen3.jpg';
import screen4 from '../screen4.jpg';

const Chapitre9Content = () => {
  return (
    <>
      <InfoBox title="Objectif du chapitre" color="indigo">
        <p className="mb-0">
          Ajout de son et équilibrage du jeu
        </p>
      </InfoBox>

      <Separator type="text" text="Ajout d'effets sonores" color="gray-darker" size="md" />
      <SectionTitle>🔊 1/ Ajout d'effets sonores</SectionTitle>

      <p>Nous allons maintenant ajouter des sons à notre jeu.<br/>
      Il y en aura 3 :</p>

      <ul className="list-disc pl-6 mb-4">
        <li>un son quand l'oiseau vole,</li>
        <li>un son quand l'oiseau meurt,</li>
        <li>un son quand l'oiseau gagne 1 point.</li>
      </ul>

      <p>Comme pour les images, on charge les sons dans la fonction <InlineCode>preload()</InlineCode> avec la fonction :</p>

      <TableContainer>
        <table className="w-full border-collapse border border-gray-300 dark:border-gray-600">
          <thead>
            <tr className="bg-gray-100 dark:bg-gray-800">
              <th className="border border-gray-300 dark:border-gray-600 p-2 text-left">Fonction</th>
              <th className="border border-gray-300 dark:border-gray-600 p-2 text-left">Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="border border-gray-300 dark:border-gray-600 p-2"><InlineCode>loadSound(nom)</InlineCode></td>
              <td className="border border-gray-300 dark:border-gray-600 p-2">Charge un son en mémoire. À mettre dans la fonction preload.<br/>- Paramètre : nom du son (sous forme de texte)<br/>- Retourne une référence vers ce son</td>
            </tr>
          </tbody>
        </table>
      </TableContainer>

      <p>Les sons sont disponibles dans l'asset manager (bouton violet en dessous de Play).<br/>
      On charge les 3 sons et on les stocke dans des variables globales :</p>

      <CodeBlock language="javascript">
{`sonVol = loadSound("vol.wav");
sonPoint = loadSound("point.wav");
sonMeurt = loadSound("meurt.wav");`}
      </CodeBlock>

      

      <Note type="exercice" title="🧪 À toi de jouer : Chargement des sons">
        <p>Crée 3 variables globales pour contenir nos sons :</p>

        <CodeBlock language="javascript">
{`let sonVol;
let sonPoint;
let sonMeurt;`}
        </CodeBlock>

        <p>Charge les sons dans <InlineCode>preload()</InlineCode> :</p>

        <CodeBlock language="javascript">
{`sonVol = loadSound("vol.wav");
sonPoint = loadSound("point.wav");
sonMeurt = loadSound("meurt.wav");`}
        </CodeBlock>
      </Note>

      <Separator type="text" text="Jouer les sons" color="gray-darker" size="md" />
      <SectionTitle>🎵 2/ Jouer les sons</SectionTitle>

      <p>Pour jouer un son, on utilise la fonction p5.js <InlineCode>.play()</InlineCode> :</p>

      <TableContainer>
        <table className="w-full border-collapse border border-gray-300 dark:border-gray-600">
          <thead>
            <tr className="bg-gray-100 dark:bg-gray-800">
              <th className="border border-gray-300 dark:border-gray-600 p-2 text-left">Fonction</th>
              <th className="border border-gray-300 dark:border-gray-600 p-2 text-left">Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="border border-gray-300 dark:border-gray-600 p-2"><InlineCode>son.play()</InlineCode></td>
              <td className="border border-gray-300 dark:border-gray-600 p-2">Joue le son <InlineCode>son</InlineCode></td>
            </tr>
          </tbody>
        </table>
      </TableContainer>

      <SubHeading>Son de l'envol</SubHeading>
      <p>Quand le joueur clique, on joue le son de l'envol :</p>

      <CodeBlock language="javascript">
{`//--------------------------------- CLIQUE ------------------------------------------
function mousePressed() {
  if (mouseX < 0 || mouseX > width || mouseY < 0 || mouseY > height) return;
  oiseau.vitesseY = oiseauImpulsion;
  sonVol.play();
}`}
      </CodeBlock>

      <SubHeading>Son de la mort</SubHeading>
      <p>Quand une collision est détectée, on joue le son de la mort :</p>

      <CodeBlock language="javascript">
{`if (VerifierCollision(t) == true) {
  Gameover();
  sonMeurt.play();
  return;
}`}
      </CodeBlock>

      <SubHeading>Son du score</SubHeading>
      <p>Quand on franchit un tuyau, on joue le son de score :</p>

      <CodeBlock language="javascript">
{`if (t.franchi == false && oiseau.x > t.x) {
  score = score + 1;
  t.franchi = true;
  sonPoint.play();
}`}
      </CodeBlock>

      <Note type="exercice" title="🧪 À toi de jouer : Jouer les sons">
        <p>Ajoute ces lignes au bon endroit :</p>

        <ul className="list-disc pl-6 mb-4">
          <li><InlineCode>sonVol.play();</InlineCode></li>
          <li><InlineCode>sonMeurt.play();</InlineCode></li>
          <li><InlineCode>sonPoint.play();</InlineCode></li>
        </ul>

        <br/>
        <Result>Un son est joué quand tu cliques, quand tu meurs, et quand tu gagnes un point.</Result>
      </Note>

      <Note type="warning" title="⚠️ Attention : Pourquoi un son est joué quand tu cliques hors du canvas ?">
        <p>Parce que <InlineCode>mousePressed()</InlineCode> est appelé même si tu cliques en dehors du canvas.<br/>
        Pour éviter ça, on vérifie que la souris est bien dans le canvas :</p>

        <CodeBlock language="javascript">
{`if (mouseX < 0 || mouseX > width || mouseY < 0 || mouseY > height) return;`}
        </CodeBlock>
      </Note>

      <Note type="exercice" title="🧪 À toi de jouer : Empêche les clics hors du canvas">
        <p>Ajoute cette condition au tout début de la fonction <InlineCode>mousePressed</InlineCode>.</p>
        <br/>
        <Result>Plus de son joué quand tu cliques en dehors du canvas.</Result>
                 <CorrectionBox>
           <CodeBlock language="javascript">
{`//--------------------------------- CLIQUE ------------------------------------------
function mousePressed() {
  if (mouseX < 0 || mouseX > width || mouseY < 0 || mouseY > height) return;
  oiseau.vitesseY = oiseauImpulsion;
  sonVol.play();
}`}
           </CodeBlock>
         </CorrectionBox>
      </Note>

      <Separator type="text" text="Équilibrage" color="gray-darker" size="md" />
      <SectionTitle>⚖️ 3/ Équilibrage</SectionTitle>

      <p>Le jeu est maintenant terminé, il ne reste plus qu'à équilibrer sa difficulté !<br/>
      C'est une étape importante pour rendre le gameplay intéressant.</p>

      <p>On peut jouer sur ces variables :</p>

      <CodeBlock language="javascript">
{`let tuyauEspace = 130;       // Espace entre les tuyaux
let tuyauVitesse = 150;      // Vitesse des tuyaux
let tuyauIntervalle = 2;     // Intervalle d'apparition des tuyaux (en secondes)
let oiseauGravite = 800;     // Gravité appliquée à l'oiseau
let oiseauImpulsion = -300;  // Force quand on clique`}
      </CodeBlock>

      <p>J’ai essayé d’équilibrer le jeu au mieux. Flappy Bird a connu beaucoup de succès justement parce qu’il est difficile à jouer, donc c’est normal que ton jeu le soit aussi par défaut !</p>

      <p>Mais si c'est trop dur, tu peux modifier ces valeurs :</p>

      <SubHeading>Agrandir l'espace entre les tuyaux :</SubHeading>
      <CodeBlock language="javascript">
{`let tuyauEspace = 170;`}
      </CodeBlock>

      <SubHeading>Espacer davantage les tuyaux :</SubHeading>
      <CodeBlock language="javascript">
{`let tuyauIntervalle = 3;`}
      </CodeBlock>

      <SubHeading>Rendre le vol de l'oiseau plus facile :</SubHeading>
      <CodeBlock language="javascript">
{`let oiseauGravite = 600;
let oiseauImpulsion = -200;`}
      </CodeBlock>

      <p>À toi de voir ce qui te plaît le plus !</p>

      <Separator type="text" text="Code final" color="gray-darker" size="md" />
      <SectionTitle>✅ 4/ Code final !!</SectionTitle>

      <CodeBlock language="javascript">
{`// Jeu 3 : Flappy Bird
let imageFond;
let imageTuyauHaut;
let imageTuyauBas;
let imageOiseau;

let sonVol;
let sonPoint;
let sonMeurt;

let tuyauEspace = 130; // Espace entre les tuyaux
let tuyauVitesse = 150; // Vitesse de déplacement des tuyaux
let tuyauIntervalle = 2; // Intervalle entre les tuyaux, en secondes
let oiseauGravite = 800; // Gravité à laquelle l'oiseau tombe
let oiseauImpulsion = -300; // Force d'impulsion quand on clique

let tuyauTemps = 0;
let tuyaux = [];
let score = 0;

// Oiseau
let oiseau = {
  x: 150,
  y: 250,
  vitesseY: 0
};

//--------------------------------- PRELOAD ------------------------------------------
function preload() {
  imageFond = loadImage("fond.png");
  imageTuyauHaut = loadImage("tuyau-haut.png");
  imageTuyauBas = loadImage("tuyau-bas.png");
  imageOiseau = loadImage("flappy1.png");

  sonVol = loadSound("vol.wav");
  sonPoint = loadSound("point.wav");
  sonMeurt = loadSound("meurt.wav");
}

//--------------------------------- SETUP ------------------------------------------
function setup() {
  createCanvas(600, 600);
}

//--------------------------------- DRAW ------------------------------------------
function draw() {
  background(imageFond);
  let dt = deltaTime / 1000;

  // Parcours la liste de tuyaux
  for (let i = tuyaux.length - 1; i >= 0; i--) {
    let t = tuyaux[i];

    // Déplace et affiche le tuyau
    t.x = t.x - tuyauVitesse * dt;
    afficherTuyaux(t);

    // Supprime s'il est complètement à gauche
    if (t.x + imageTuyauHaut.width < 0) {
      tuyaux.splice(i, 1);
    }

    // Vérifie si l’oiseau vient de passer le tuyau
    if (t.franchi == false &&  oiseau.x > t.x) {
      score = score + 1;
      t.franchi = true;
      sonPoint.play();
    }

    // Vérifie les collisions
    if (VerifierCollision(t) == true) {
      Gameover();
      sonMeurt.play();
      return;
    }
  }

  // Créer un nouveau tuyau toutes les x secondes
  tuyauTemps = tuyauTemps - dt;
  if (tuyauTemps <= 0) {
    tuyauTemps = tuyauIntervalle;
    let positionX = width;
    let positionY = random(100, 400);
    tuyaux.push({ x: positionX, y: positionY, franchi: false });
  }

  // Déplacement de l'oiseau
  oiseau.vitesseY = oiseau.vitesseY + oiseauGravite * dt;
  oiseau.y = oiseau.y + oiseau.vitesseY * dt;
  image(imageOiseau, oiseau.x, oiseau.y);

  // Affichage du score
  textAlign(CENTER, TOP);
  textSize(64);
  textStyle(BOLD);
  fill(255);
  text(score, width / 2, 20);
}

//--------------------------------- CLIQUE ------------------------------------------
function mousePressed() {
  if (mouseX < 0 || mouseX > width || mouseY < 0 || mouseY > height) return;
  oiseau.vitesseY = oiseauImpulsion;
  sonVol.play();
}

//--------------------------------- AFFICHER TUYAUX ------------------------------------------
function afficherTuyaux(t) {
  image(imageTuyauHaut, t.x, t.y - imageTuyauHaut.height);
  image(imageTuyauBas, t.x, t.y + tuyauEspace);
}

//--------------------------------- COLLISION ------------------------------------------
function VerifierCollision(t) {
  // Collision avec le tuyau du haut
  if (
    oiseau.x + imageOiseau.width > t.x &&
    oiseau.x < t.x + imageTuyauHaut.width &&
    oiseau.y < t.y
  ) {
    return true;
  }

  // Collision avec le tuyau du bas
  if (
    oiseau.x + imageOiseau.width > t.x &&
    oiseau.x < t.x + imageTuyauBas.width &&
    oiseau.y + imageOiseau.height > t.y + tuyauEspace
  ) {
    return true;
  }

  // Collision avec le haut de l'écran
  if (oiseau.y < 0) {
    return true;
  }

  // Collision avec le bas de l'écran
  if (oiseau.y + imageOiseau.height > height) {
    return true;
  }

  // Pas de collision
  return false;
}

//--------------------------------- ECRAN DE FIN -------------------------------------
function Gameover() {
  noLoop();
  background(imageFond);
  fill(255, 255, 255);
  textSize(48);
  text("💀 GAME OVER 💀", width / 2, height / 2 - 40);
  textSize(32);
  text("Score final : " + score, width / 2, height / 2 + 10);
}

`}
      </CodeBlock>

    

      <Note type="info" title="✨ Félicitations !">
      <p>Tu viens de terminer ton troisième jeu !<br/>
      Visuellement, il est plus joli et plus abouti que les deux premiers, car tu utilises des images et du son 🎉</p>
      <br/>
        <p>Tu as appris plein de choses :</p>
        

        <ul className="list-none space-y-1">
          <li>✅ Gérer des images et des sons</li>
          <li>✅ Créer un système de score</li>
          <li>✅ Gérer les collisions</li>
          <li>✅ Gérer la physique de l'oiseau</li>
          <li>✅ Ajouter des effets sonores</li>
          <li>✅ Équilibrer un jeu pour le rendre plus fun</li>
        </ul>
<br/>
        <p>N'hésite pas à revoir les différents chapitres si tu as un doute,<br/>
        ou à venir sur Discord pour poser des questions si tu veux aller plus loin.</p>
<br/>
        <p><strong>Bravo à toi, tu peux être fier de ton travail !</strong><br/>
        Et maintenant… RDV pour le prochain jeu 😄🎮</p>
      </Note>
    </>
  );
};

export default Chapitre9Content; 